// AWS SDK for interacting with AWS services
import { tokenRefreshManager } from "./tokenRefresh";
const AWS = require("aws-sdk");

/**
 * Generates a unique filename for S3 uploads
 * @param {string} action - The type of action being performed (defaults to 'new_signal')
 * @returns {string} A unique filename containing timestamp, random string and action suffix
 */
const generateUniqueFileName = (action = 'new_signal') => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  const actionSuffix = action === 'new_signal' ? '-ns' : '';
  
  return `${timestamp}-${randomSuffix}${actionSuffix}`;
};

let globalCredentials = null;
let globalS3 = null;
let lastTokenRefresh = 0; // Track when credentials were last refreshed

/**
 * Initializes AWS S3 with Cognito credentials if not already initialized
 * @param {Object} apiData - Contains AWS configuration like identity pool and user pool
 * @param {Object} user - User data containing idToken for authentication
 */
const initializeS3 = async (apiData, user) => {
  // Check if credentials need to be refreshed (every 50 minutes to be safe)
  const now = Date.now();
  const shouldRefresh = !globalCredentials || 
                       !globalS3 ||
                       (now - lastTokenRefresh) > (50 * 60 * 1000) || // 50 minutes
                       !globalCredentials.accessKeyId;

  if (shouldRefresh) {
    // Set up Cognito credentials using identity pool and user token
    globalCredentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: apiData?.api?.identityPool,
      Logins: {
        [`cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${apiData?.api?.userPool}`]: user?.idToken,
      },
    });

    // Configure AWS SDK with region and credentials
    AWS.config.update({
      region: process.env.REACT_APP_AWS_REGION,
      credentials: globalCredentials,
    });

    // Get fresh credentials
    await globalCredentials.getPromise();
    
    // Initialize S3 client
    globalS3 = new AWS.S3();
    lastTokenRefresh = now;
  }
};

/**
 * Uploads data to S3 bucket
 * @param {Object} data - The data to upload
 * @param {Object} apiData - AWS configuration data
 * @param {Object} user - User authentication data
 * @param {string} action - Type of action being performed (defaults to 'new_signal')
 * @returns {Promise<Object>} Upload result containing success status and file details
 */
const uploadToS3 = async (data, apiData, user, action = 'new_signal') => {
  try {
    await initializeS3(apiData, user);

    const dataString = JSON.stringify(data);
    const bucketName = apiData?.websocket?.socketBucket;
    
    if (!bucketName) {
      throw new Error("S3 bucket name not found in apiData");
    }

    let s3FileName = generateUniqueFileName(action);
    
    const uploadParams = {
      Body: dataString,
      Bucket: bucketName,
      ContentType: 'application/json',
      Key: s3FileName,
    };

    const response = await globalS3.upload(uploadParams).promise();
    
    return {
      success: true,
      fileName: s3FileName,
      location: response.Location
    };

  } catch (error) {
    // If it's a token expiration error, try to refresh credentials and retry once
    if (error.message && error.message.includes("Invalid login token")) {
      
      try {
        // Mark the token refresh attempt for tracking
        await tokenRefreshManager.refreshUserToken(user);
        
        // Force credential refresh - AWS SDK will attempt to refresh automatically
        globalCredentials = null;
        globalS3 = null;
        lastTokenRefresh = 0;
        
        await initializeS3(apiData, user);
        
        const dataString = JSON.stringify(data);
        const bucketName = apiData?.websocket?.socketBucket;
        let s3FileName = generateUniqueFileName(action);
        
        const uploadParams = {
          Body: dataString,
          Bucket: bucketName,
          ContentType: 'application/json',
          Key: s3FileName,
        };

        const response = await globalS3.upload(uploadParams).promise();
        
        return {
          success: true,
          fileName: s3FileName,
          location: response.Location
        };
      } catch (retryErr) {
        console.error("Retry failed after token refresh:", retryErr);
        return {
          success: false,
          error: `Upload failed after token refresh: ${retryErr.message}`
        };
      }
    }
    
    return {
      success: false,
      error: error.message
    };
  }
};

const resetS3Credentials = () => {
  globalCredentials = null;
  globalS3 = null;
  lastTokenRefresh = 0;
};

export { uploadToS3, resetS3Credentials }; 